// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

import { ClassValue, clsx } from "clsx";
import { CSSProperties, MutableRefObject } from "react";
import { twMerge } from "tailwind-merge";

export type ClassNames<Keys extends string> = Partial<Record<Keys, string>>;

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function stylesMerge(...inputs: (CSSProperties | undefined)[]): CSSProperties | undefined {
	if (inputs.length === 0) {
		return undefined;
	}

	let result: CSSProperties | undefined = undefined;
	for (const input of inputs) {
		if (result == null) {
			result = input;
		} else if (input != null) {
			result = { ...result, ...input };
		}
	}
	return result;
}

/**
 * This will return the raw numeric value of a string, omitting `px`, `em`, `rem`, etc. from the string.
 */
export function getNumericValue(value: string | undefined): number | undefined {
	if (value == null) {
		return undefined;
	}

	if (value?.endsWith("%")) {
		throw new Error("Percentage values are not supported");
	}

	try {
		return parseFloat(value.replace(/[(px|em|rem)]/g, ""));
	} catch (error) {
		return undefined;
	}
}

type MergeableRef<T> = ((instance: T | null) => void) | MutableRefObject<T | null> | null;
/**
 * Use to merge multiple refs into one when using forwardRef.
 * Useful when you need to reference an element but also need to pass the parents ref to a child component.
 *
 * @example
 * const ref1 = useRef(null);
 * const Component = forwardRef((props, ref) => {
 *  const localRef = useRef(null);
 *
 *  const mergedRef = mergeRefs(ref1, localRef, ref);
 *
 *  return <div ref={mergedRef} />
 * });
 *
 * @param refs list of element refs to merge
 * @returns a function that merges the refs
 */
export function mergeRefs<T>(...refs: Array<MergeableRef<T> | undefined>) {
	return (node: T) => {
		for (const ref of refs) {
			if (!ref) {
				continue;
			}

			if (typeof ref === "object") {
				ref.current = node;
			} else {
				ref(node);
			}
		}
	};
}

// JSON-stringifies the object with a stable order of object's keys (and nested
// objects' keys).
export function jsonHash(object: any): string {
	return JSON.stringify(object, (key, value) => {
		if (typeof value === "object" && value !== null && !(value instanceof Array)) {
			return Object.keys(value)
				.sort(localeCompare)
				.reduce((result, key) => {
					result[key] = value[key];
					return result;
				}, {} as any);
		}
		return value;
	});
}

/**
 * Use to type-safe check if a value is defined.
 * Helps when filtering undefined or null values from arrays:
 *
 * @example
 * const array = [1,2,undefined,3] // (number | undefined)[]
 * array.filter(isDefined); // number[]
 */
export function isDefined<T>(value: T | undefined | null): value is T {
	return value != null;
}

type RemoveEmptyObject<T> = T extends {} ? (keyof T extends never ? never : T) : T;
/**
 * Use to type-safe check if an object is not empty and remove {} from unions.
 *
 * @example
 * function myFn(value: {} | { foo: 'bar'}) {
 * 	if (isNotEmptyObject(value)) {
 * 		// value is { foo: 'bar' }
 * 		value.foo; // Safely access foo
 * 	}
 * }
 */
export function isNotEmptyObject<T>(val: T): val is RemoveEmptyObject<T> {
	return val != null;
}

export function localeCompare(a: string | undefined | null, b: string | undefined | null): number {
	return (a ?? "").localeCompare(b ?? "");
}


// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

/**
 * The identity functions returns whatever is being passed into it.
 */
export const identity = <T>(value: T): T => value;

export const noop = () => {};

export const getNoop = () => noop;

export const alwaysTrue = () => true;

export const alwaysFalse = () => false;

export const alwaysNull = () => null;

export const alwaysEmptyString = () => "";

/**
 * map allows you to map from one value to another. It looks stupid, but comes in
 * handy within JSX. See `splitMetricValueAndUnit` for an example.
 */
export function map<IN, OUT>(value: IN, fn: (value: IN) => OUT): OUT {
	return fn(value);
}

/**
 * wrapIf wraps a value with a function if the value is truthy, returns the value otherwise.
 * This is useful when you want to conditionally wrap a JSX element with another element.
 *
 * @example
 * ```tsx
 * function MyCode(props: { someOptionalRecord?: { href: string } }) {
 * 	return (
 * 		<div>
 * 			{wrapIf(<span>My Content</span>, props.someOptionalRecord, (content, record) => (
 * 				<a href={record.href}>{content}</a>
 * 			))}
 * 		</div>
 * 	);
 * }
 * ```
 */
export function wrapIf<CONTENT, CONDITION>(
	content: CONTENT,
	condition: CONDITION,
	fn: (content: CONTENT, condition: NonNullable<CONDITION>) => CONTENT
): CONTENT {
	if (condition != null && condition !== false) {
		return fn(content, condition);
	}

	return content;
}
